import React, { useState, useRef, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  FlatList,
  Dimensions,
  ViewToken,
} from 'react-native';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { Event, Photo } from '@/types/residents/event';
import { useEventRsvp } from '@/hooks/residents/useEventRsvp';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { AddToCalendarBottomSheet } from '@/components/residents/events/mobile/AddToCalendarBottomSheet';
import {
  formatEventDateShort,
  formatEventTimeRange,
  getEventBadgeInfo,
  getEventCapacityText,
  getEventDescription,
  EventBadgeStyle,
} from '@/utils/residents/events/eventHelpers';
import { eventStrings } from '@/constants/residents/strings';
import { GlassCard } from '../GlassCard';

const CalendarIcon = RESIDENTS_SVG_COMPONENTS.CALENDAR;
const ClockIcon = RESIDENTS_SVG_COMPONENTS.CLOCK;
const LocationIcon = RESIDENTS_SVG_COMPONENTS.LOCATION;
const GroupIcon = RESIDENTS_SVG_COMPONENTS.GROUP;
const ChevronDown = RESIDENTS_SVG_COMPONENTS.CHEVRON_DOWN;
const ChevronUp = RESIDENTS_SVG_COMPONENTS.CHEVRON_UP;
const CheckIcon = RESIDENTS_SVG_COMPONENTS.CHECK;

const IMAGE_SIZE = Dimensions.get('window').width - 72;

interface EventGlassCardProps {
  event: Event;
  isPast?: boolean;
}

export const EventGlassCard: React.FC<EventGlassCardProps> = React.memo(({ event, isPast = false }) => {
  const { colors } = useThemeColors();

  const BADGE_BG: Record<EventBadgeStyle, string> = {
    attending: colors.accent.greenBg,
    gradient: colors.accent.redBg,
    neutral: colors.glass.buttonBg,
    full: colors.glass.buttonBg,
  };

  const BADGE_TEXT_COLOR: Record<EventBadgeStyle, string> = {
    attending: colors.accent.green,
    gradient: colors.accent.red,
    neutral: colors.text.primary,
    full: colors.text.muted,
  };
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showCalendarModal, setShowCalendarModal] = useState(false);

  const { accept, decline, isUpdating } = useEventRsvp(event, {
    acceptSuccessMessage: 'Marked as going',
    declineSuccessMessage: 'Updated',
  });

  const photos = event.photos || [];
  const hasMultipleImages = photos.length > 1;

  const onViewableItemsChanged = useRef(({ viewableItems }: { viewableItems: ViewToken[] }) => {
    if (viewableItems.length > 0 && viewableItems[0].index !== null) {
      setActiveImageIndex(viewableItems[0].index);
    }
  }).current;

  const viewabilityConfig = useRef({ itemVisiblePercentThreshold: 50 }).current;

  const badgeInfo = getEventBadgeInfo(event, isPast);
  const capacityText = getEventCapacityText(event);
  const description = getEventDescription(event);
  const shouldTruncate = description.length > 100;
  const displayDescription = showFullDescription || !shouldTruncate
    ? description
    : description.substring(0, 100) + '...';

  const handleOpenCalendar = useCallback(() => setShowCalendarModal(true), []);
  const handleCloseCalendar = useCallback(() => setShowCalendarModal(false), []);

  const renderImageItem = ({ item }: { item: Photo }) => (
    <Image source={{ uri: item.photoUrl }} style={styles.carouselImage} resizeMode="cover" />
  );

  return (
    <GlassCard borderRadius={16} padding={0}>
      {isUpdating && (
        <View style={styles.loadingOverlay}>
          <LoadingSpinner size="small" showMessage={false} variant="resident" />
        </View>
      )}

      {/* Image Section */}
      <View style={styles.imageContainer}>
        {photos.length > 0 ? (
          <View style={styles.imageWrapper}>
            <FlatList
              data={photos}
              renderItem={renderImageItem}
              keyExtractor={(item) => item.id.toString()}
              horizontal
              pagingEnabled
              showsHorizontalScrollIndicator={false}
              onViewableItemsChanged={onViewableItemsChanged}
              viewabilityConfig={viewabilityConfig}
              style={styles.carousel}
            />
            {hasMultipleImages && (
              <View style={styles.carouselDotsContainer}>
                <View style={styles.carouselDotsBackground}>
                  {photos.map((_, index) => (
                    <View
                      key={index}
                      style={[styles.carouselDot, index === activeImageIndex && styles.carouselDotActive]}
                    />
                  ))}
                </View>
              </View>
            )}
          </View>
        ) : (
          <View style={[styles.imageWrapper, styles.placeholderImage, { backgroundColor: colors.bg.base }]}>
            <CalendarIcon width={32} height={32} color={colors.text.dimmer} />
            <Text style={[styles.placeholderText, { color: colors.text.dimmer }]}>No Image</Text>
          </View>
        )}
      </View>

      {/* Info Section */}
      <View style={styles.infoContainer}>
        <View style={[styles.summaryContainer, { borderBottomColor: colors.separator }]}>
          {/* Badge + Calendar */}
          <View style={styles.headerRow}>
            {badgeInfo && (
              <View style={[
                styles.badge,
                { backgroundColor: BADGE_BG[badgeInfo.style], borderColor: colors.glass.buttonBorder },
              ]}>
                {badgeInfo.showCheckmark && CheckIcon && (
                  <CheckIcon width={12} height={12} color={BADGE_TEXT_COLOR[badgeInfo.style]} />
                )}
                <Text style={[styles.badgeText, { color: BADGE_TEXT_COLOR[badgeInfo.style] }]}>
                  {badgeInfo.text}
                </Text>
              </View>
            )}
            {!isPast && (
              <TouchableOpacity onPress={handleOpenCalendar} style={[styles.calendarButton, { backgroundColor: colors.glass.buttonBg, borderColor: colors.glass.buttonBorder }]}>
                <CalendarIcon width={20} height={20} color={PRIMITIVE_COLORS.resioBlue} />
              </TouchableOpacity>
            )}
          </View>

          {/* Title */}
          <Text style={[styles.title, { color: colors.text.primary }]}>{event.name || 'Event'}</Text>

          {/* Info Grid */}
          <View style={styles.infoGrid}>
            <View style={styles.infoColumn}>
              <View style={styles.infoItem}>
                <CalendarIcon width={18} height={18} color={colors.text.subtle} />
                <Text style={[styles.infoText, { color: colors.text.secondary }]}>{formatEventDateShort(event.startTime)}</Text>
              </View>
              <View style={styles.infoItem}>
                <ClockIcon width={18} height={18} color={colors.text.subtle} />
                <Text style={[styles.infoText, { color: colors.text.secondary }]}>{formatEventTimeRange(event.startTime, event.endTime)}</Text>
              </View>
            </View>
            <View style={styles.infoColumn}>
              {event.location && (
                <View style={styles.infoItem}>
                  <LocationIcon width={18} height={18} color={colors.text.subtle} />
                  <Text style={[styles.infoText, { color: colors.text.secondary }]} numberOfLines={1}>{event.location}</Text>
                </View>
              )}
              {capacityText && (
                <View style={styles.infoItem}>
                  <GroupIcon width={18} height={18} color={colors.text.subtle} />
                  <Text style={[styles.infoText, { color: colors.text.secondary }]}>{capacityText}</Text>
                </View>
              )}
            </View>
          </View>

          {/* RSVP Button */}
          {!isPast && (
            <View style={styles.buttonsContainer}>
              {event.rsvp ? (
                <TouchableOpacity
                  style={[styles.button, styles.buttonOutline]}
                  onPress={decline}
                  disabled={isUpdating}
                >
                  <Text style={styles.buttonOutlineText}>{eventStrings.buttons.cantAttend}</Text>
                </TouchableOpacity>
              ) : (
                <TouchableOpacity
                  style={[styles.button, styles.buttonPrimary]}
                  onPress={accept}
                  disabled={isUpdating}
                >
                  <Text style={styles.buttonPrimaryText}>{eventStrings.buttons.willAttend}</Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </View>

        {/* Description */}
        {description.length > 0 && (
          <View style={styles.descriptionContainer}>
            <Text style={[styles.descriptionText, { color: colors.text.subtle }]}>{displayDescription}</Text>
            {shouldTruncate && (
              <TouchableOpacity
                style={styles.showMoreContainer}
                onPress={() => setShowFullDescription(!showFullDescription)}
              >
                <Text style={styles.showMoreText}>
                  {showFullDescription ? eventStrings.showLess : eventStrings.showMore}
                </Text>
                {showFullDescription ? (
                  <ChevronUp width={16} height={16} color={PRIMITIVE_COLORS.resioBlue} />
                ) : (
                  <ChevronDown width={16} height={16} color={PRIMITIVE_COLORS.resioBlue} />
                )}
              </TouchableOpacity>
            )}
          </View>
        )}
      </View>

      <AddToCalendarBottomSheet
        visible={showCalendarModal}
        onClose={handleCloseCalendar}
        event={event}
      />
    </GlassCard>
  );
});

EventGlassCard.displayName = 'EventGlassCard';

const styles = StyleSheet.create({
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.35)',
    zIndex: 2,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 16,
  },
  imageContainer: {
    padding: 12,
    alignItems: 'center',
  },
  imageWrapper: {
    width: IMAGE_SIZE,
    height: IMAGE_SIZE * 0.65,
    borderRadius: 12,
    overflow: 'hidden',
  },
  carousel: {
    width: IMAGE_SIZE,
    height: IMAGE_SIZE * 0.65,
  },
  carouselImage: {
    width: IMAGE_SIZE,
    height: IMAGE_SIZE * 0.65,
  },
  placeholderImage: {
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
  },
  placeholderText: {
    ...REDESIGN_TYPOGRAPHY.caption,
  },
  carouselDotsContainer: {
    position: 'absolute',
    bottom: 12,
    left: 0,
    right: 0,
    alignItems: 'center',
  },
  carouselDotsBackground: {
    flexDirection: 'row',
    backgroundColor: 'rgba(0,0,0,0.5)',
    borderRadius: 20,
    paddingHorizontal: 8,
    paddingVertical: 6,
    gap: 6,
  },
  carouselDot: {
    width: 7,
    height: 7,
    borderRadius: 3.5,
    backgroundColor: 'rgba(255,255,255,0.35)',
  },
  carouselDotActive: {
    backgroundColor: '#FFFFFF',
  },
  infoContainer: {
    paddingHorizontal: 20,
    paddingTop: 4,
    paddingBottom: 20,
    gap: 16,
  },
  summaryContainer: {
    gap: 16,
    borderBottomWidth: 1,
    paddingBottom: 16,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 60,
    borderWidth: 1,
  },
  badgeText: {
    ...REDESIGN_TYPOGRAPHY.captionMedium,
  },
  calendarButton: {
    width: 36,
    height: 36,
    borderRadius: 10,
    borderWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 'auto',
  },
  title: {
    ...REDESIGN_TYPOGRAPHY.cardTitleLarge,
    fontSize: 18,
    lineHeight: 24,
  },
  infoGrid: {
    flexDirection: 'row',
    gap: 20,
  },
  infoColumn: {
    flex: 1,
    gap: 10,
  },
  infoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  infoText: {
    ...REDESIGN_TYPOGRAPHY.body,
    flex: 1,
  },
  buttonsContainer: {
    flexDirection: 'row',
  },
  button: {
    flex: 1,
    height: REDESIGN_SPACING.buttonHeight - 10,
    borderRadius: REDESIGN_SPACING.buttonRadius,
    justifyContent: 'center',
    alignItems: 'center',
  },
  buttonPrimary: {
    backgroundColor: PRIMITIVE_COLORS.resioBlue,
  },
  buttonPrimaryText: {
    ...REDESIGN_TYPOGRAPHY.button,
    color: '#FFFFFF',
  },
  buttonOutline: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: PRIMITIVE_COLORS.resioBlue,
  },
  buttonOutlineText: {
    ...REDESIGN_TYPOGRAPHY.button,
    color: PRIMITIVE_COLORS.resioBlue,
  },
  descriptionContainer: {
    gap: 8,
  },
  descriptionText: {
    ...REDESIGN_TYPOGRAPHY.small,
    lineHeight: 18,
  },
  showMoreContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  showMoreText: {
    ...REDESIGN_TYPOGRAPHY.captionMedium,
    color: PRIMITIVE_COLORS.resioBlue,
  },
});
